import React, { useEffect, useRef, useState } from "react";
import { Box, keyframes, SxProps, Theme } from "@mui/material";

const TICKER_GAP_PX = 64;

const marqueeSeamlessLoop = keyframes`
    0%   { transform: translateX(0); }
    100% { transform: translateX(var(--ticker-offset)); }
`;

const marqueeFullViewport = keyframes`
    0%   { transform: translateX(100vw); }
    100% { transform: translateX(-100%); }
`;

export interface ITickerConfig {
    speed?: number;
    minDuration?: number;
    animationStyle?: "seamless-loop" | "full-viewport";
    alwaysAnimate?: boolean;
    onAnimationIteration?: () => void;
}

interface ITickerTextProps {
    text?: string;
    children?: React.ReactNode;
    config?: ITickerConfig;
    sx?: SxProps<Theme>;
    textSx?: SxProps<Theme>;
    measureTrigger?: number;
    overlay?: React.ReactNode;
}

const DEFAULT_SPEED = 20;
const DEFAULT_MIN_DURATION = 10;

export const TickerText = ({ text, children, config = {}, sx, textSx, measureTrigger, overlay }: ITickerTextProps) => {
    const {
        speed = DEFAULT_SPEED,
        minDuration = DEFAULT_MIN_DURATION,
        animationStyle = "seamless-loop",
        alwaysAnimate = false,
        onAnimationIteration,
    } = config;

    const contentRef = useRef<HTMLElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [measurement, setMeasurement] = useState({
        shouldAnimate: false,
        isReady: false,
        tickerOffset: 0,
        animationDuration: 0,
    });

    const { shouldAnimate, isReady, tickerOffset, animationDuration } = measurement;

    const isFullViewport = animationStyle === "full-viewport";
    const isSeamlessLoop = !isFullViewport;

    useEffect(() => {
        const contentEl = contentRef.current;
        const containerEl = containerRef.current;

        if (!contentEl || !containerEl) {
            return;
        }

        const measure = () => {
            const contentWidth = contentEl.scrollWidth;
            const containerWidth = containerEl.clientWidth;
            const shouldScroll = alwaysAnimate || contentWidth > containerWidth;

            if (!shouldScroll) {
                setMeasurement({ shouldAnimate: false, isReady: true, tickerOffset: 0, animationDuration: 0 });
                return;
            }

            if (isFullViewport) {
                const viewportWidth = globalThis.window?.innerWidth ?? 1000;
                setMeasurement({
                    shouldAnimate: true,
                    isReady: true,
                    tickerOffset: 0,
                    animationDuration: Math.max(minDuration, (contentWidth + viewportWidth) / speed),
                });
            } else {
                const offset = contentWidth + TICKER_GAP_PX;
                setMeasurement({
                    shouldAnimate: true,
                    isReady: true,
                    tickerOffset: offset,
                    animationDuration: Math.max(minDuration, offset / speed),
                });
            }
        };

        measure();

        if (alwaysAnimate) {
            const observer = new ResizeObserver(measure);
            observer.observe(contentEl);
            return () => {
                observer.disconnect();
            };
        }
    }, [alwaysAnimate, isFullViewport, measureTrigger, minDuration, speed, text]);

    const animationKeyframe = isFullViewport ? marqueeFullViewport : marqueeSeamlessLoop;
    const animationValue = shouldAnimate ? `${animationKeyframe} ${animationDuration}s linear infinite` : "none";

    const renderContent = (isHiddenCopy?: boolean) => {
        if (children !== undefined) {
            return (
                <Box
                    ref={isHiddenCopy ? undefined : (contentRef as React.RefObject<HTMLDivElement>)}
                    component="span"
                    aria-hidden={isHiddenCopy || undefined}
                    sx={{ display: "inline-flex", alignItems: "center", ...textSx }}
                >
                    {children}
                </Box>
            );
        }

        return (
            <Box
                ref={isHiddenCopy ? undefined : (contentRef as React.RefObject<HTMLSpanElement>)}
                component="span"
                aria-hidden={isHiddenCopy || undefined}
                sx={{
                    whiteSpace: "nowrap",
                    ...(isHiddenCopy ? { pl: `${TICKER_GAP_PX}px` } : {}),
                    ...textSx,
                }}
            >
                {text}
            </Box>
        );
    };

    return (
        <React.StrictMode>
            <Box ref={containerRef} sx={{ overflow: "hidden", position: "relative", ...sx }}>
                <Box
                    component="span"
                    style={
                        {
                            "--ticker-offset": isSeamlessLoop ? `-${tickerOffset}px` : "0",
                            ...(!isReady && isFullViewport && { visibility: "hidden" as const }),
                        } as React.CSSProperties
                    }
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                        animation: animationValue,
                        "&:hover": { animationPlayState: "paused" },
                    }}
                    onAnimationIteration={onAnimationIteration}
                >
                    {renderContent()}
                    {shouldAnimate && isSeamlessLoop && renderContent(true)}
                </Box>

                {shouldAnimate && overlay}
            </Box>
        </React.StrictMode>
    );
};
